import { Fragment, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  isNavigationItemActive,
  type NavigationItem,
} from "@/shared/config/navigationConfig";
import { Icons } from "@/shared/icons";
import { useToastStore } from "@/shared/stores/toastStore";
import {
  DrawerBackdrop,
  DrawerCloseButton,
  DrawerHeader,
  DrawerLayer,
  DrawerNavigation,
  DrawerNavigationItem,
  DrawerNavigationItemDescription,
  DrawerPanel,
  DrawerTitle,
} from "./MobileNavigationDrawer.styles";

interface MobileNavigationDrawerProps {
  isOpen: boolean;
  items: NavigationItem[];
  onClose: () => void;
}

const getUnavailableDescriptionId = (itemId: string) =>
  `mobile-navigation-drawer-${itemId.toLowerCase()}-description`;

const getUnavailableDescription = (item: NavigationItem) =>
  item.hasPermission
    ? "Esta sección todavía no se encuentra disponible."
    : "No cuentas con permisos para acceder a esta sección.";

export const MobileNavigationDrawer = ({
  isOpen,
  items,
  onClose,
}: MobileNavigationDrawerProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const showToast = useToastStore((state) => state.showToast);
  const panelRef = useRef<HTMLElement>(null);
  const activeItemRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const initialFocusTarget =
      activeItemRef.current ?? closeButtonRef.current;
    initialFocusTarget?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab" || !panelRef.current) {
        return;
      }

      const focusableElements = Array.from(
        panelRef.current.querySelectorAll<HTMLButtonElement>(
          "button:not(:disabled)",
        ),
      );
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (!firstFocusable || !lastFocusable) {
        return;
      }

      if (event.shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastFocusable
      ) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleNavigate = (item: NavigationItem) => {
    if (!item.hasPermission) {
      showToast({
        type: "error",
        title: "NO CUENTAS CON PERMISOS PARA ACCEDER A ESTA SECCIÓN",
      });
      return;
    }

    if (!item.released) {
      showToast({
        type: "info",
        title: `PRÓXIMAMENTE: ${item.modalLabel ?? item.id}`,
      });
      return;
    }

    onClose();
    navigate(item.route);
  };

  return (
    <DrawerLayer
      id="mobile-navigation-drawer"
      data-testid="mobile-navigation-drawer"
    >
      <DrawerBackdrop aria-hidden="true" onClick={onClose} />
      <DrawerPanel
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navegación principal"
      >
        <DrawerHeader>
          <DrawerTitle>Menú</DrawerTitle>
          <DrawerCloseButton
            ref={closeButtonRef}
            type="button"
            aria-label="Cerrar menú principal"
            onClick={onClose}
          >
            <Icons name="iconClose" width={24} height={24} />
          </DrawerCloseButton>
        </DrawerHeader>

        <DrawerNavigation aria-label="Secciones">
          {items.map((item) => {
            const isActive = isNavigationItemActive(location.pathname, item);
            const isUnavailable = !item.released || !item.hasPermission;
            const unavailableDescriptionId = isUnavailable
              ? getUnavailableDescriptionId(item.id)
              : undefined;

            return (
              <Fragment key={item.id}>
                <DrawerNavigationItem
                  ref={isActive ? activeItemRef : undefined}
                  type="button"
                  $active={isActive}
                  $unavailable={isUnavailable}
                  aria-current={isActive ? "page" : undefined}
                  aria-disabled={isUnavailable}
                  aria-describedby={unavailableDescriptionId}
                  onClick={() => handleNavigate(item)}
                >
                  <Icons
                    name={item.icon}
                    width={28}
                    height={28}
                    isHovered={isActive}
                  />
                  <span>{item.name}</span>
                </DrawerNavigationItem>
                {unavailableDescriptionId ? (
                  <DrawerNavigationItemDescription
                    id={unavailableDescriptionId}
                  >
                    {getUnavailableDescription(item)}
                  </DrawerNavigationItemDescription>
                ) : null}
              </Fragment>
            );
          })}
        </DrawerNavigation>
      </DrawerPanel>
    </DrawerLayer>
  );
};
